import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import * as commonApi from '../../api/app/commonApi';
import * as api from '../../api/app/monitoredCounterScrapApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { AssetFullPathHeader } from '../../components/core/AssetFullPathHeader/AssetFullPathHeader';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { AssetDetailTabValues, MonitoredPanelValues } from '../../types/app/asset.types';
import {
  MonitoredCounterScrap,
  MonitoredCounterScrapInputs,
} from '../../types/app/monitoredCounterScrap.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
import { MonitoredCounterScrapForm } from './index';
import { useStyles } from './MonitoredCounterScrapForm.styles';

export function MonitoredCounterScrapDetail() {
  const { data: isUsedHydra } = useQuery<boolean>('useHydra', commonApi.useHydraDataService);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id, parentId } = useParams();
  const [defaultValues, setDefaultValues] = useState<MonitoredCounterScrapInputs | null>(null);

  const { mutate: mutateUpdateMonitoredCounterScrap } = useMutation(
    api.updateMonitoredCounterScrap,
  );
  const { data, isLoading, isError } = useQuery<MonitoredCounterScrap>(
    ['monitoredCounterScrap', id],
    () => api.getMonitoredCounterScrap(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      parentAssetId: data.parentAssetId,
      opcClientId: data.opcClientId,
      influxValueName: data.influxValueName,
      nodeId: data.nodeId,
      name: data.name,
      main: data.main,
      useMultipleWithHydraPartitioning: data.useMultipleWithHydraPartitioning,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<MonitoredCounterScrapInputs> = (
    data: MonitoredCounterScrapInputs,
  ) => {
    if (!defaultValues) return;

    mutateUpdateMonitoredCounterScrap(
      { ...data },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.MONITORED_COUNTER_SCRAP_EDITED, { variant: 'success' });
          navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
            state: {
              tabValue: AssetDetailTabValues.MONITORED_VALUES,
              panelValue: MonitoredPanelValues.MONITORED_COUNTER_SCRAP,
            },
          });
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues || isUsedHydra === undefined) return <Loader />;

  return (
    <>
      <AssetFullPathHeader
        finalTitle={dictionary.MONITORED_COUNTER_SCRAP_EDIT}
        parentId={Number(parentId)}
      />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid item xs={12} md={8} lg={4}>
              <Paper className={classes.paper}>
                <MonitoredCounterScrapForm
                  onSubmit={handleOnSubmit}
                  isUsedHydra={isUsedHydra}
                  onBack={() =>
                    navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
                      state: {
                        tabValue: AssetDetailTabValues.MONITORED_VALUES,
                        panelValue: MonitoredPanelValues.MONITORED_COUNTER_SCRAP,
                      },
                    })
                  }
                  defaultValues={defaultValues}
                />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
